import { useQuery } from '@apollo/client';
import type { ReactNode } from 'react';
import React, { useEffect, useState } from 'react';
import DeltaPreview from '../components/DeltaPreview';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import {
  SEARCH_PLATFORM_INFO_MAP,
  SearchPreviewMode,
} from '../integrations/IntegrationPlatformInfo';
import { FloatingContentImage } from '../utils/contentPreviewPopOverSize';
import Link from '../utils/router/Link';
import { stripHighlight } from '../utils/search/renderHighlight';
import styles from './ExternalSearchPreview.module.scss';

const PREVIEW_FRAGMENT = gql(`
  fragment ExternalSearchPreview_preview on MentionPreview {
    content
    imageUrl
  }
`);

const EXTERNAL_SEARCH_PREVIEW_QUERY = gql(`
  query externalSearchPreview($externalId: String!, $platform: String!) {
    externalSearchPreview(externalId: $externalId, platform: $platform) {
      imageUrl
      content
    }
  }
`);

/**
 * Preload preview data so that we can show the preview only when the data is ready
 * to avoid preview resizing after the preview is shown.
 */
const usePreloadPreviewData = (
  preview?: DocumentType<typeof PREVIEW_FRAGMENT> | null,
) => {
  const [state, setState] = useState<'loading' | 'loaded' | 'failed'>(
    'loading',
  );

  const [previousContent, setPreviousContent] = useState(preview?.content);

  if (preview?.content !== previousContent && preview?.content) {
    setState('loaded');
    setPreviousContent(preview?.content);
  }

  useEffect(() => {
    if (preview?.imageUrl) {
      Object.assign(new Image(), {
        src: preview.imageUrl,
      })
        .decode()
        .then(
          () => {
            setState('loaded');
          },
          () => {
            setState('failed');
          },
        );
    }
  }, [preview]);

  return state;
};

interface ExternalSearchPreviewProps {
  externalId: string;
  icon: React.ReactNode;
  platform: string;
  preview?: DocumentType<typeof PREVIEW_FRAGMENT> | null;
  title: string;
  url: string;
  updatePopOver: () => void;
}

const ExternalSearchPreview = ({
  externalId,
  icon,
  platform,
  preview: localPreview,
  title,
  url,
  updatePopOver,
}: ExternalSearchPreviewProps) => {
  const { searchPreview } = SEARCH_PLATFORM_INFO_MAP[platform] || {};
  const { data: { externalSearchPreview: remotePreview } = {}, loading } =
    useQuery(EXTERNAL_SEARCH_PREVIEW_QUERY, {
      variables: { externalId, platform },
      skip: !!localPreview || searchPreview !== SearchPreviewMode.Single,
    });

  const preview = localPreview || remotePreview;
  const dataState = usePreloadPreviewData(preview);

  let content: ReactNode = null;
  if ((!loading && !preview) || dataState === 'failed') {
    content = (
      <div className={styles.emptyContainer}>
        <div>No preview available</div>
      </div>
    );
  } else if (preview?.imageUrl && dataState !== 'loading') {
    content = (
      <div className={styles.imageContainer}>
        <FloatingContentImage
          src={preview.imageUrl}
          alt={stripHighlight(title)}
          onLoad={updatePopOver}
        />
      </div>
    );
  } else if (preview?.content && dataState !== 'loading') {
    content = (
      <div className={styles.contentContainer}>
        <DeltaPreview
          typography="searchResult"
          content={preview.content}
          link={false}
        />
      </div>
    );
  }

  return (
    content && (
      <Link
        to={url}
        target="_blank"
        className={styles.container}
        rel="noreferrer"
      >
        <div>{content}</div>
        <div className={styles.footer}>
          <div className={styles.icon}>{icon}</div>
          <div className={styles.title}>{stripHighlight(title)}</div>
        </div>
      </Link>
    )
  );
};

export default ExternalSearchPreview;
